<template>
  <div>
    <transition name="scroll-y-transition" mode="out-in">
      <form :key="searchDTO.advanced">
        <v-row no-gutters class="mt-4">
          <v-col v-if="!isSingleCourtMode" cols="12">
            <c-autocomplete
              :items="courts"
              v-model="searchDTO.courtID"
              item-text="displayName"
              item-value="resourceID"
              :label="$t('page.searchCriteria.calendar.field.court')"
              key="courtID"
              @change="changeCourt"
              outlined
              hide-details="auto"
              aria-describedby="fe0d2b9b-d89c-4c0f-8cc3-d9646f5e0987"/>
            <field-description id="fe0d2b9b-d89c-4c0f-8cc3-d9646f5e0987"
                               message-key="page.searchCriteria.calendar.field.court.description"/>
          </v-col>
          <v-col v-if="!isSingleCalendarLocationMode" cols="12">
            <c-autocomplete
              v-model="searchDTO.calendar.locationID"
              :items="calendarLocations"
              item-text="locationName"
              item-value="locationID"
              :label="$t('page.searchCriteria.calendar.field.location')"
              :no-data-text="$t('global.courtFirst')"
              outlined
              hide-details="auto"
              aria-describedby="bc614254-3be9-4db0-8499-c802a2e64ce4"/>
            <field-description id="bc614254-3be9-4db0-8499-c802a2e64ce4"
                               message-key="page.searchCriteria.calendar.field.location.description"/>
          </v-col>
          <v-col cols="12">
            <c-autocomplete
              v-model="searchDTO.calendar.courtSessionTypeID"
              :items="courtSessionTypes"
              item-text="courtSessionTypeName"
              item-value="courtSessionTypeID"
              :label="$t('page.searchCriteria.calendar.field.sessionType')"
              outlined
              hide-details="auto"
              aria-describedby="acdef06a-9d4e-4968-98c1-3f0037da4aeb"/>
            <field-description id="acdef06a-9d4e-4968-98c1-3f0037da4aeb"
                               message-key="page.searchCriteria.calendar.field.sessionType.description"/>
          </v-col>
          <v-col cols="12">
            <c-autocomplete
              v-model="searchDTO.calendar.departmentActorInstanceUUID"
              :items="departments"
              item-text="departmentName"
              item-value="actorInstanceUUID"
              :label="$t('page.searchCriteria.calendar.field.department')"
              :no-data-text="$t('global.courtFirst')"
              outlined
              hide-details="auto"
              aria-describedby="a474ae8a-3769-4f46-9889-7948d01142fb"/>
            <field-description id="a474ae8a-3769-4f46-9889-7948d01142fb"
                               message-key="page.searchCriteria.calendar.field.department.description"/>
          </v-col>
          <v-col cols="12">
            <date-range-picker
              :label="$t('page.searchCriteria.calendar.field.date')"
              :labelStart="$t('page.searchCriteria.calendar.field.dateFrom')"
              :labelEnd="$t('page.searchCriteria.calendar.field.dateTo')"
              keyPrefix="calendarDate"
              offer-future-dates
              :inputAttributes="{ outlined: true }"
              :choice.sync="searchDTO.calendar.calendarDateChoice"
              :startDate.sync="searchDTO.calendar.calendarDateStart"
              :endDate.sync="searchDTO.calendar.calendarDateEnd"
              aria-describedby="a36446ea-605e-4b75-afab-7fe2d0159a67"/>
            <field-description id="a36446ea-605e-4b75-afab-7fe2d0159a67"
                               message-key="page.searchCriteria.calendar.field.date.description"/>
          </v-col>
          <v-col cols="12">
            <c-text-field
              :label="$t('page.searchCriteria.calendar.field.name')"
              v-model="searchDTO.calendar.calendarName"
              maxlength="250"
              outlined
              hide-details="auto"
              aria-describedby="ad4e5d90-93a5-4197-b8e6-8123d38107c4"/>
            <field-description id="ad4e5d90-93a5-4197-b8e6-8123d38107c4"
                               message-key="page.searchCriteria.calendar.field.name.description"/>
          </v-col>
          <v-col v-if="searchDTO.advanced" cols="12">
            <c-autocomplete
              v-model="searchDTO.calendar.calendarNameQueryTypeID"
              :label="$t('page.searchCriteria.calendar.field.calendarNameSearchType')"
              :items="queryTypes"
              outlined
              class="ml-8"
              :clearable="false"
              hide-details="auto"
              aria-describedby="a22f8c25-6d74-4ae5-8094-4a5cbdc51d12"/>
            <field-description id="a22f8c25-6d74-4ae5-8094-4a5cbdc51d12" class="ml-8"
                               message-key="global.queryType.description"/>
          </v-col>
          <template v-if="searchDTO.advanced">
            <v-col cols="12">
              <c-text-field
                :label="$t('page.searchCriteria.calendar.field.judgeFirstName')"
                v-model="searchDTO.calendar.judgeFirstName"
                maxlength="250"
                outlined
                hide-details="auto"
                aria-describedby="a07ba21e-2858-4b68-af8d-8d23c1b76d52"/>
              <field-description id="a07ba21e-2858-4b68-af8d-8d23c1b76d52"
                                 message-key="page.searchCriteria.calendar.field.judgeFirstName.description"/>
            </v-col>
            <v-col cols="12">
              <c-autocomplete
                :label="$t('page.searchCriteria.calendar.field.judgeFirstNameSearchType')"
                v-model="searchDTO.calendar.judgeFirstNameQueryTypeID"
                :items="queryTypes"
                outlined
                class="ml-8"
                :clearable="false"
                hide-details="auto"
                aria-describedby="a498495e-76fa-4a1d-92e0-0b43e3f31541"/>
              <field-description id="a498495e-76fa-4a1d-92e0-0b43e3f31541" class="ml-8"
                                 message-key="global.queryType.description"/>
            </v-col>
            <v-col cols="12">
              <c-text-field
                :label="$t('page.searchCriteria.calendar.field.judgeLastName')"
                v-model="searchDTO.calendar.judgeLastName"
                outlined
                maxlength="250"
                hide-details="auto"
                aria-describedby="a9dd46c3-da5d-4a58-b5e4-697a9be25fbd"/>
              <field-description id="a9dd46c3-da5d-4a58-b5e4-697a9be25fbd"
                                 message-key="page.searchCriteria.calendar.field.judgeLastName.description"/>
            </v-col>
            <v-col cols="12">
              <c-autocomplete
                :label="$t('page.searchCriteria.calendar.field.judgeLastNameSearchType')"
                v-model="searchDTO.calendar.judgeLastNameQueryTypeID"
                :items="queryTypes"
                outlined
                class="ml-8"
                :clearable="false"
                hide-details="auto"
                aria-describedby="aa284af1-f496-4f4b-b060-ea010a6b7eb8"/>
              <field-description id="aa284af1-f496-4f4b-b060-ea010a6b7eb8" class="ml-8"
                                 message-key="global.queryType.description"/>
            </v-col>
          </template>
          <template v-else>
            <v-col cols="12">
              <c-text-field
                :label="$t('page.searchCriteria.calendar.field.judgeName')"
                v-model="searchDTO.calendar.judgeName"
                outlined
                maxlength="250"
                hide-details="auto"
                aria-describedby="aaf6d4c2-c2ee-4227-8edd-3dcde3abbbb5"/>
              <field-description id="aaf6d4c2-c2ee-4227-8edd-3dcde3abbbb5"
                                 message-key="page.searchCriteria.calendar.field.judgeName.description"/>
            </v-col>
          </template>
        </v-row>
      </form>
    </transition>
  </div>
</template>

<script>
import _ from 'lodash';
import axios from 'axios';
import FieldDescription from '@components/support/FieldDescription';
import DateRangePicker from '@components/support/DateRangePicker';
import SearchCriteriaMixin from '@/mixins/searchCriteria';
import { useApplicationStore } from '@/stores/applicationStore';

const applicationStore = useApplicationStore();

export default {
  components: { FieldDescription, DateRangePicker },
  mixins: [ SearchCriteriaMixin ],
  data () {
    return {
      departments: [],
      courtSessionTypes: []
    };
  },
  computed: {
    calendarLocations () {
      return applicationStore.getCalendarLocationsByCourt(this.searchDTO.courtID);
    },
    isSingleCalendarLocationMode () {
      return applicationStore.isSingleCalendarLocationMode;
    }
  },
  created () {
    axios.get(this.$cove.getAPI({ name: 'api.courtSessionTypes' }))
      .then((response) => {
        this.courtSessionTypes = _.defaultTo(response.data, []);
      });
  },
  watch: {
    'searchDTO.courtID': {
      immediate: true,
      handler (newValue) {
        if (!_.isNil(newValue))
        {
          axios.get(this.$cove.getAPI({ name: 'api.court.departments', params: { courtID: newValue } }))
            .then((response) => {
              this.departments = _.defaultTo(response.data, []);
            });
        }
        else
        {
          this.departments = [];
        }
      }
    },
    'searchDTO.advanced' (newValue) {
      if (newValue)
      {
        this.searchDTO.calendar.judgeName = null;
        this.searchDTO.calendar.judgeFirstNameQueryTypeID = this.getDefaultQueryType(true);
        this.searchDTO.calendar.judgeLastNameQueryTypeID = this.getDefaultQueryType(true);

        // only change this query type if they didn't enter anything yet
        if (this.searchDTO.calendar.calendarName === null)
        {
          this.searchDTO.calendar.calendarNameQueryTypeID = this.getDefaultQueryType(true);
        }
      }
    }
  },
  methods: {
    changeCourt () {
      this.searchDTO.calendar.locationID = null;
      this.searchDTO.calendar.departmentActorInstanceUUID = null;
    }
  }
};
</script>
